import React, { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { listProperties } from '../admin/properties.js';
import { CONTACT_EMAIL } from '../seo.js';
import '../main.css';

const INITIAL_FORM_VALUES = {
  name: '',
  property: '',
  email: '',
  phone: '',
  message: '',
};

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
}

function validateForm(values) {
  const nextErrors = {};

  if (!String(values.name || '').trim()) {
    nextErrors.name = 'A name is required.';
  }

  if (!String(values.property || '').trim()) {
    nextErrors.property = 'Please select a property.';
  }

  if (!String(values.email || '').trim()) {
    nextErrors.email = 'An email is required.';
  } else if (!validateEmail(values.email)) {
    nextErrors.email = 'Email is not valid.';
  }

  if (!String(values.phone || '').trim()) {
    nextErrors.phone = 'A phone number is required.';
  }

  if (!String(values.message || '').trim()) {
    nextErrors.message = 'A message is required.';
  }

  return nextErrors;
}

function getEmailJsErrorMessage(error) {
  const rawMessage = String(error?.text || error?.message || '').trim();

  if (!rawMessage) {
    return 'We could not send your message right now. Please try again in a moment.';
  }

  return `We could not send your message right now. ${rawMessage}`;
}

export const ContactUs = () => {
  const form = useRef();
  const [properties, setProperties] = useState([]);
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitState, setSubmitState] = useState({ type: '', message: '' });
  const [propertiesError, setPropertiesError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadProperties() {
      try {
        const nextProperties = await listProperties();

        if (isMounted) {
          setProperties(nextProperties);
          setPropertiesError('');
        }
      } catch (error) {
        if (isMounted) {
          setProperties([]);
          setPropertiesError('Property options are temporarily unavailable. You can still contact us and mention the property in your message.');
        }
      }
    }

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setFieldErrors((currentErrors) => {
      if (!currentErrors[name]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });

    setSubmitState((currentState) =>
      currentState.type === 'error' ? { type: '', message: '' } : currentState
    );
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    const nextErrors = validateForm(formValues);

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setSubmitState({
        type: 'error',
        message: 'Please complete the required fields before sending your message.',
      });
      return;
    }

    setFieldErrors({});
    setSubmitState({ type: '', message: '' });
    setIsSubmitting(true);

    try {
      await emailjs.sendForm('service_9gak2vh', 'template_mwlyhby', form.current, {
        publicKey: '53g7OfuLrEV9aEqNE',
      });

      setSubmitState({
        type: 'success',
        message: 'Your message was sent successfully.',
      });
      form.current.reset();
      setFormValues(INITIAL_FORM_VALUES);
    } catch (error) {
      console.log('FAILED...', error?.text || error?.message || error);
      setSubmitState({
        type: 'error',
        message: getEmailJsErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
  <section className="page-section" id="contact">
    <div className="container">
      <div className="text-center">
        <h2 className="section-heading text-uppercase">Contact Us</h2>
      </div>
      {propertiesError ? (
        <div className="text-center text-warning mb-4">{propertiesError}</div>
      ) : null}
      <form id="contactForm" ref={form} onSubmit={sendEmail} noValidate>
        <input name="to_email" type="hidden" value={CONTACT_EMAIL} />
        <div className="row align-items-stretch mb-5">
          <div className="col-md-6">
            <div className="form-group">
              <input
                className={`form-control${fieldErrors.name ? ' is-invalid' : ''}`}
                id="name"
                name="name"
                type="text"
                placeholder="Your Name *"
                value={formValues.name}
                onChange={handleFieldChange}
                required
                aria-invalid={fieldErrors.name ? 'true' : 'false'}
              />
              <div className="invalid-feedback">{fieldErrors.name || 'A name is required.'}</div>
            </div>
            
            <div className="form-group">
              <select
                className={`form-control form-group-drop${fieldErrors.property ? ' is-invalid' : ''}`}
                id="property"
                name="property"
                onChange={handleFieldChange}
                value={formValues.property}
                required
                aria-invalid={fieldErrors.property ? 'true' : 'false'}
              >
                <option value="">Select Property *</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.label}>
                    {property.label}
                  </option>
                ))}
                <option value="Any availability">Any availability</option>
              </select>
              <div className="invalid-feedback">{fieldErrors.property || 'Please select a property.'}</div>
            </div>
            <div className="form-group">
              <input
                className={`form-control${fieldErrors.email ? ' is-invalid' : ''}`}
                id="email"
                name="email"
                type="email"
                placeholder="Your Email *"
                value={formValues.email}
                onChange={handleFieldChange}
                required
                aria-invalid={fieldErrors.email ? 'true' : 'false'}
              />
              <div className="invalid-feedback">{fieldErrors.email || 'An email is required.'}</div>
            </div>

            <div className="form-group mb-md-0">
              <input
                className={`form-control${fieldErrors.phone ? ' is-invalid' : ''}`}
                id="phone"
                name="phone"
                type="tel"
                placeholder="Your Phone *"
                value={formValues.phone}
                onChange={handleFieldChange}
                required
                aria-invalid={fieldErrors.phone ? 'true' : 'false'}
              />
              <div className="invalid-feedback">{fieldErrors.phone || 'A phone number is required.'}</div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group form-group-textarea">
              <textarea
                className={`form-control${fieldErrors.message ? ' is-invalid' : ''}`}
                id="message"
                name="message"
                placeholder="Your Message *"
                value={formValues.message}
                onChange={handleFieldChange}
                required
                aria-invalid={fieldErrors.message ? 'true' : 'false'}
              ></textarea>
              <div className="invalid-feedback">{fieldErrors.message || 'A message is required.'}</div>
            </div>
          </div>
        </div>
        {submitState.message ? (
          <div className={`text-center mb-3 ${submitState.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {submitState.message}
          </div>
        ) : null}
        <div className="text-center">
          <button
            className="btn btn-primary btn-xl text-uppercase"
            id="submitButton"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </form>
    </div>
  </section>
  
  );
};

export default ContactUs;
